/**
 * StaffAuditTail (GH #2317)
 *
 * The audit tail on the staff detail page: the lifecycle and role events
 * recorded against one staff membership, newest first, read through
 * listStaffAuditEvents (fi-staff-lifecycle 1.3.0). It replaces the
 * placeholder that said a per-user audit list would ship in Sprint 6, while
 * the page's dialogs already told the FI admin each action "is recorded on the
 * staff member's audit trail".
 *
 * Renders only what the server returns: the action, who acted, when, and the
 * details the spec allowlists for that action. An absent value stays absent;
 * an actor with no display name on record reads as such, never as a guess.
 *
 * Composes @aaimplatform/ui primitives (Alert, Button, EmptyState).
 */

import { History as NoEventsIcon } from 'lucide-react';
import { Alert, Button, EmptyState } from '@aaimplatform/ui';
import {
  useStaffAuditEvents,
  type StaffAuditAction,
  type StaffAuditEvent,
} from '../../hooks/useStaffLifecycle';

/** One label per spec action; a new StaffAuditAction value fails tsc here. */
const ACTION_LABELS: Record<StaffAuditAction, string> = {
  STAFF_SUSPENDED: 'Suspended',
  STAFF_REACTIVATED: 'Reactivated',
  STAFF_OFFBOARDED: 'Off-boarded',
  STAFF_CREDENTIAL_RESET: 'Sign-in credential reset',
  STAFF_ROLE_GRANTED: 'Role granted',
  STAFF_ROLE_REVOKED: 'Role revoked',
};

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Who acted, from the event's actor; null fields are said to be null. */
export function describeActor(actor: StaffAuditEvent['actor']): string {
  if (actor.displayName) return actor.displayName;
  if (actor.userId) return 'Name not on record';
  return 'No actor recorded';
}

/** The details lines for one event, in reading order. */
export function describeDetails(event: StaffAuditEvent): string[] {
  const { details } = event;
  const lines: string[] = [];
  if (details.roleSlug) lines.push(`Role: ${details.roleSlug}`);
  if (typeof details.revokedInvitationCount === 'number') {
    const count = details.revokedInvitationCount;
    lines.push(`${count} pending invitation${count === 1 ? '' : 's'} revoked`);
  }
  if (details.resetInvitationExpiresAt) {
    lines.push(`Reset invitation expires ${formatTimestamp(details.resetInvitationExpiresAt)}`);
  }
  if (details.reason) lines.push(`Reason: ${details.reason}`);
  return lines;
}

export interface StaffAuditTailProps {
  /** The staff membership id (Staff.id), the path id of listStaffAuditEvents. */
  staffId: string;
}

export function StaffAuditTail({ staffId }: StaffAuditTailProps) {
  const tail = useStaffAuditEvents(staffId);
  const events = tail.data?.pages.flatMap((page) => page.items) ?? [];

  if (tail.isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div
          role="status"
          aria-label="Loading audit events"
          className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"
        />
      </div>
    );
  }

  if (tail.isError && events.length === 0) {
    return (
      <Alert type="error" title="Unable to load audit events">
        {tail.error?.message ?? 'The audit events for this staff member could not be loaded.'}
      </Alert>
    );
  }

  if (events.length === 0) {
    return (
      <EmptyState
        icon={<NoEventsIcon className="h-8 w-8" aria-hidden />}
        title="No events recorded yet"
        description="Suspensions, reactivations, off-boarding, credential resets and role changes for this staff member are listed here once they happen."
      />
    );
  }

  return (
    <div>
      <ol className="space-y-2" aria-label="Audit events">
        {events.map((event) => (
          <li key={event.id} className="px-3 py-2 border border-recessed rounded-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-sans font-semibold text-primary">
                {ACTION_LABELS[event.action]}
              </span>
              <time dateTime={event.occurredAt} className="text-xs font-sans text-tertiary">
                {formatTimestamp(event.occurredAt)}
              </time>
            </div>
            <p className="mt-1 text-xs font-sans text-secondary">
              By {describeActor(event.actor)}
            </p>
            {describeDetails(event).map((line) => (
              <p key={line} className="text-xs font-sans text-secondary">
                {line}
              </p>
            ))}
          </li>
        ))}
      </ol>

      {tail.isError && (
        <div className="mt-3">
          <Alert type="error" title="Unable to load older events">
            {tail.error?.message ?? 'Older audit events could not be loaded.'}
          </Alert>
        </div>
      )}

      {tail.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            disabled={tail.isFetchingNextPage}
            onClick={() => {
              void tail.fetchNextPage();
            }}
          >
            {tail.isFetchingNextPage ? 'Loading older events...' : 'Show older events'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default StaffAuditTail;
